/**
 * Express Checkout Hook
 *
 * Provides express checkout functionality for the shopping cart.
 * Allows users to pay for their entire cart using digital wallets
 * (Apple Pay, Google Pay, PayPal) directly from the cart drawer.
 *
 * This hook handles:
 * - Device availability checks for each payment method
 * - Adyen checkout/component creation
 * - Order creation and payment processing
 * - Error handling and state management
 *
 * @example
 * ```tsx
 * const {
 *   hasApplePay,
 *   hasGooglePay,
 *   hasPayPal,
 *   isProcessing,
 *   error,
 *   initiateCheckout,
 * } = useExpressCheckout({
 *   onSuccess: (orderId) => router.push(checkoutPaths.confirmation(orderId)),
 *   onError: (error) => console.error(error),
 * });
 *
 * // In cart drawer
 * {hasApplePay && (
 *   <button onClick={() => initiateCheckout('applepay')}>
 *     Apple Pay
 *   </button>
 * )}
 * ```
 */

import type { UIElement } from '@adyen/adyen-web';
import { useRouter } from '@/lib/i18n/navigation';
import { useParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import { checkoutPaths } from '@/lib/paths';

import { usePaymentMethodAvailability } from '@/hooks/usePaymentMethodAvailability';
import { usePaymentProcessor } from '@/hooks/usePaymentProcessor';
import {
  createAdyenCheckout,
  createAdyenComponent,
  getAdyenComponentConfig,
  resolveAdyenLocale,
  toMinorUnits,
} from '@/lib/checkout/payments/adyen';
import { PAYMENT_TYPES, type PaymentType } from '@/lib/checkout/payments/types';
import { devError } from '@/lib/utils/devLog';
import { checkoutState } from '@/states/checkout';

import type { UseExpressCheckoutOptions, UseExpressCheckoutResult } from './types';

/**
 * Hook for express checkout from the shopping cart.
 *
 * Extracts payment orchestration logic for testability.
 * UI components can focus on presentation while this hook
 * handles all the payment flow complexity.
 */
export function useExpressCheckout({
  onSuccess,
  onError,
  onStart,
}: UseExpressCheckoutOptions = {}): UseExpressCheckoutResult {
  const router = useRouter();
  const params = useParams();
  const lang = params.lang as string;

  // Cart state
  const snap = useSnapshot(checkoutState);
  const checkoutId = snap.id || snap.checkout?.id;
  const totalAmount = snap.checkout?.totalPrice?.gross?.amount || snap.subtotal || 0;
  const currency = snap.checkout?.totalPrice?.gross?.currency || snap.currency || 'USD';
  const countryCode = snap.checkout?.shippingAddress?.country?.code || 'US';

  // Availability
  const { isMethodAvailable, isLoading: isCheckingAvailability } = usePaymentMethodAvailability();
  const hasApplePay = isMethodAvailable(PAYMENT_TYPES.APPLE_PAY);
  const hasGooglePay = isMethodAvailable(PAYMENT_TYPES.GOOGLE_PAY);
  const hasPayPal = isMethodAvailable(PAYMENT_TYPES.PAYPAL);
  const hasAnyMethod = hasApplePay || hasGooglePay || hasPayPal;

  // Local state
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeMethod, setActiveMethod] = useState<PaymentType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const componentRef = useRef<UIElement | null>(null);

  // Adyen locale
  const adyenLocale = resolveAdyenLocale(lang || 'us-en');

  // Cart validation
  const isCartValid = Boolean(checkoutId && totalAmount > 0);

  // Payment processor
  const { handlePayment, handleAdditionalDetails, createOrder } = usePaymentProcessor({
    checkoutId: checkoutId || '',
    totalAmount,
    currency,
    countryCode,
    locale: lang,
    onSuccess: (orderId) => {
      setIsProcessing(false);
      setActiveMethod(null);
      onSuccess?.(orderId);
      router.push(checkoutPaths.confirmation(orderId));
    },
    onError: (errorMsg) => {
      setError(errorMsg);
      setIsProcessing(false);
      setActiveMethod(null);
      onError?.(errorMsg);
    },
  });

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Initiate express checkout with the specified payment method
   */
  const initiateCheckout = useCallback(
    async (paymentMethod: PaymentType) => {
      // Validate cart
      if (!checkoutId) {
        const errorMsg = 'No checkout found';
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      if (totalAmount <= 0) {
        const errorMsg = 'Cart is empty';
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      // Start processing
      setIsProcessing(true);
      setActiveMethod(paymentMethod);
      setError(null);
      onStart?.();

      try {
        const amount = toMinorUnits(totalAmount, currency);

        // Create Adyen checkout
        const adyenCheckout = await createAdyenCheckout({
          countryCode,
          price: { amount, currency },
          locale: adyenLocale,
          formatMessage: (msg) => msg.defaultMessage || '',
          paymentMethodType: paymentMethod,
          showPayButton: true,
          onSubmit: async (state, element, actions) => {
            try {
              // Create order from checkout
              const orderId = await createOrder(checkoutId);
              if (!orderId) {
                actions.reject();
                setError('Failed to create order');
                return;
              }

              // Process payment
              const result = await handlePayment({
                paymentType: paymentMethod,
                paymentData: state.data,
                handleAction: (element as UIElement).handleAction,
                element: element as UIElement,
              });

              if (result.success) {
                actions.resolve({ resultCode: 'Authorised' });
              } else {
                actions.reject();
                setError(result.error || 'Payment failed');
              }
            } catch (err) {
              actions.reject();
              setError(err instanceof Error ? err.message : 'Payment failed');
            }
          },
          onAdditionalDetails: async (state, element) => {
            await handleAdditionalDetails(state as Record<string, unknown>, element as UIElement);
          },
          onError: (err) => {
            if (err.name !== 'CANCEL') {
              setError(err.message);
              onError?.(err.message);
            }
            setIsProcessing(false);
            setActiveMethod(null);
          },
        });

        // Get component configuration
        const componentConfig = getAdyenComponentConfig(
          paymentMethod,
          { amount, currency },
          countryCode
        );

        // Create payment component
        const component = await createAdyenComponent(paymentMethod, adyenCheckout, componentConfig);

        componentRef.current = component;

        // Submit to trigger wallet UI
        await component.submit();
      } catch (err) {
        devError('[useExpressCheckout] Error:', err);
        const errorMsg = err instanceof Error ? err.message : 'Failed to initialize payment';
        setError(errorMsg);
        setIsProcessing(false);
        setActiveMethod(null);
        onError?.(errorMsg);
      }
    },
    [
      checkoutId,
      totalAmount,
      currency,
      countryCode,
      adyenLocale,
      createOrder,
      handlePayment,
      handleAdditionalDetails,
      onStart,
      onError,
    ]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      componentRef.current?.unmount();
      componentRef.current = null;
    };
  }, []);

  return {
    hasApplePay,
    hasGooglePay,
    hasPayPal,
    hasAnyMethod,
    isCheckingAvailability,
    isProcessing,
    activeMethod,
    error,
    isCartValid,
    totalAmount,
    currency,
    initiateCheckout,
    clearError,
  };
}
